import useSWR from 'swr';
import { ApiError, apiRequest, getTenantId } from './api.js';

/**
 * `tenantId` names the tenant for this call. `null` makes it a platform-wide call that sends no
 * tenant header even when a tenant is selected (see apiRequest). `undefined` uses the selection.
 */
export async function readJson<T>(
  path: string,
  init?: RequestInit,
  tenantId?: string | null,
): Promise<T> {
  const request: RequestInit =
    tenantId === undefined
      ? (init ?? {})
      : {
          ...init,
          headers: {
            ...(init?.headers as Record<string, string> | undefined),
            'X-Tenant-Id': tenantId ?? '',
          },
        };
  const response = await apiRequest(path, request);
  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error?: { message?: string } }).error?.message ?? response.status)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return body as T;
}

/**
 * SWR wrapper. The key includes the tenant so switching tenants never reuses cached rows.
 * `tenantId` overrides the selected tenant for one call (platform admin looking at a client).
 * `null` marks platform-wide resources (tenant list, plans): the key then ignores the selected
 * tenant so the data does not disappear while the selection changes.
 */
export function useRemote<T>(path: string | null, tenantId?: string | null) {
  const tenant = tenantId === null ? null : (tenantId ?? getTenantId());
  return useSWR(path ? ([path, tenant] as const) : null, ([key, scopedTenant]) =>
    readJson<T>(key, undefined, tenantId === null ? null : (scopedTenant ?? undefined)),
  );
}

export async function downloadCsv(path: string, filename: string): Promise<void> {
  await saveDownload(path, filename, { Accept: 'text/csv' });
}

export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  await saveDownload(path, fallbackName);
}

async function saveDownload(
  path: string,
  fallbackName: string,
  headers?: Record<string, string>,
): Promise<void> {
  const response = await apiRequest(path, headers ? { headers } : {});
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error?: { message?: string } }).error?.message ?? response.status)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameOf(response.headers.get('Content-Disposition'), fallbackName);
  link.click();
  URL.revokeObjectURL(url);
}

function filenameOf(disposition: string | null, fallbackName: string): string {
  const match = /filename="([^"]+)"/.exec(disposition ?? '');
  const name = match?.[1];
  if (!name || name.includes('/') || name.includes('\\') || name.includes('..'))
    return fallbackName;
  return name;
}
